import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, RefreshCw, Trash2, Check, AlertCircle, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DnsSetupWizard, type DnsWizardDomain } from "@/components/dns-setup-wizard";
import { InfraStorefront } from "@/components/infra-storefront";
import { Separator } from "@/components/ui/separator";

interface SendingDomain {
  id: string;
  domain: string;
  purpose: "primary" | "cold";
  fromAddress: string | null;
  spfVerified: boolean;
  dkimVerified: boolean;
  returnPathVerified: boolean;
  verificationStatus: string;
  dkimHost: string | null;
  dkimTextValue: string | null;
  returnPathHost: string | null;
  returnPathCname: string | null;
  verifiedAt: string | null;
  lastVerificationCheckAt: string | null;
}

type SendingDomainsResponse = SendingDomain[] | { domains?: SendingDomain[] };

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string") return err;
  return fallback;
}

export function SendingDomainsTab({ showRequestCard = true }: { showRequestCard?: boolean } = {}) {
  const { toast } = useToast();
  const [domain, setDomain] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [returnPath, setReturnPath] = useState("");
  const [wizardDomainId, setWizardDomainId] = useState<string | null>(null);

  const { data: domains = [], isLoading } = useQuery<SendingDomainsResponse, Error, SendingDomain[]>({
    queryKey: ["/api/admin/sending-domains"],
    select: (raw) => (Array.isArray(raw) ? raw : raw?.domains ?? []),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/sending-domains", {
        domain,
        purpose: "cold",
        fromAddress: fromAddress || undefined,
        returnPath: returnPath || undefined,
      });
      return res.json();
    },
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sending-domains"] });
      setDomain("");
      setFromAddress("");
      setReturnPath("");
      toast({ title: "Sending domain added", description: "Use the setup wizard to publish the DNS records." });
      // Auto-open the wizard for the freshly-created domain.
      const newId = created?.id || created?.domain?.id;
      if (newId) setWizardDomainId(newId);
    },
    onError: (err: unknown) => {
      toast({ title: "Could not add domain", description: errorMessage(err, "Failed"), variant: "destructive" });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/sending-domains/${id}/verify`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sending-domains"] });
      toast({ title: "Verification refreshed" });
    },
    onError: (err: unknown) => {
      toast({ title: "Verification failed", description: errorMessage(err, "Failed"), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/sending-domains/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sending-domains"] });
    },
  });

  const StatusBadge = ({ ok, label }: { ok: boolean; label: string }) => (
    <Badge variant="outline" className={ok ? "text-green-600 border-green-500/30" : "text-amber-600 border-amber-500/30"} data-testid={`badge-status-${label.toLowerCase()}`}>
      {ok ? <Check className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
      {label}
    </Badge>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cold Sending Domain</CardTitle>
          <CardDescription>
            Cold outreach must use a dedicated subdomain (e.g. <code>send.yourcompany.com</code>) so a stale list can't damage your transactional reputation. Set up a CNAME for DKIM and an SPF include record at your DNS host.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="sd-domain">Subdomain</Label>
            <Input
              id="sd-domain"
              placeholder="send.yourcompany.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
              data-testid="input-sending-domain"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sd-from">From Address (optional)</Label>
            <Input
              id="sd-from"
              placeholder={domain ? `outreach@${domain}` : "outreach@send.yourcompany.com"}
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value.trim())}
              data-testid="input-sending-from"
            />
            <p className="text-xs text-muted-foreground">Defaults to <code>outreach@</code> the subdomain above. The address must end in the subdomain.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sd-return-path">Return-Path (optional)</Label>
            <Input
              id="sd-return-path"
              placeholder={domain ? `pm-bounces.${domain}` : "pm-bounces.send.yourcompany.com"}
              value={returnPath}
              onChange={(e) => setReturnPath(e.target.value.trim().toLowerCase())}
              data-testid="input-sending-return-path"
            />
            <p className="text-xs text-muted-foreground">Custom Return-Path host for bounce handling. Leave blank to use the provider default.</p>
          </div>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!domain || createMutation.isPending}
            className="w-full"
            data-testid="button-add-sending-domain"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add cold sending domain
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : domains.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No sending domains configured yet.</p>
      ) : (
        <div className="space-y-3">
          {domains.map((d) => (
            <Card key={d.id} data-testid={`card-sending-domain-${d.id}`}>
              <CardContent className="py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm" data-testid={`text-domain-${d.id}`}>{d.domain}</p>
                    <p className="text-xs text-muted-foreground">{d.purpose === "cold" ? "Cold outreach" : "Primary"} • {d.fromAddress || "no From address"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="default" onClick={() => setWizardDomainId(d.id)} data-testid={`button-wizard-${d.id}`}>
                      <Sparkles className="w-4 h-4 mr-1" />
                      Setup wizard
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => verifyMutation.mutate(d.id)} disabled={verifyMutation.isPending} data-testid={`button-verify-${d.id}`}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${verifyMutation.isPending ? "animate-spin" : ""}`} />
                      Verify
                    </Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteMutation.mutate(d.id)} data-testid={`button-delete-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge ok={d.spfVerified} label="SPF" />
                  <StatusBadge ok={d.dkimVerified} label="DKIM" />
                  <StatusBadge ok={d.returnPathVerified} label="Return-Path" />
                </div>
                {(d.dkimHost || d.returnPathHost) && (
                  <div className="rounded-md bg-muted/40 p-3 text-xs space-y-2 font-mono">
                    {d.dkimHost && (
                      <div>
                        <div className="text-muted-foreground font-sans">DKIM TXT</div>
                        <div data-testid={`text-dkim-host-${d.id}`}>{d.dkimHost}</div>
                        <div className="break-all" data-testid={`text-dkim-value-${d.id}`}>{d.dkimTextValue}</div>
                      </div>
                    )}
                    {d.returnPathHost && (
                      <div>
                        <div className="text-muted-foreground font-sans">Return-Path CNAME</div>
                        <div data-testid={`text-rp-host-${d.id}`}>{d.returnPathHost} → {d.returnPathCname}</div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DnsSetupWizard
        open={!!wizardDomainId}
        onOpenChange={(o) => { if (!o) setWizardDomainId(null); }}
        domain={(domains.find((d) => d.id === wizardDomainId) as DnsWizardDomain | undefined) ?? null}
      />

      {/* ── Cold-email purchase wizard (Task #397) ──
          The live self-serve auto-provision storefront (InfraStorefront) is now
          the default cold-email setup path for tenants, replacing the manual
          beta request card. Hidden when the host already renders the storefront
          alongside this component (e.g. the Cold Email area). */}
      {showRequestCard && (
        <>
          <Separator className="my-2" />
          <InfraStorefront surface="tenant" />
        </>
      )}
    </div>
  );
}
